import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { BlobServiceClient } from '@azure/storage-blob';

const WorkforceApplicationForm = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    experience: '',
    location: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Azure Storage configuration (same as ApplicationForm)
  const AZURE_STORAGE_URL = 'https://xpertstgacc.blob.core.windows.net/xps?sp=racwdli&st=2026-03-11T09:52:11Z&se=2027-03-11T18:07:11Z&spr=https&sv=2024-11-04&sr=c&sig=vH6IKKLhyVVYeEkbpf%2FOBKg6pKF1vWzfXLZhTj%2FoIYg%3D';

  // Parse SAS token details
  const parseSASToken = () => {
    try {
      const url = new URL(AZURE_STORAGE_URL);
      const params = new URLSearchParams(url.search);
      
      const sasDetails = {
        permissions: params.get('sp'), // racwdli = read, add, create, write, delete, list, immutable
        startTime: params.get('st'),
        expiryTime: params.get('se'),
        protocol: params.get('spr'),
        version: params.get('sv'),
        resource: params.get('sr'), // c = container
        signature: params.get('sig')
      };

      // Parse dates
      const startDate = sasDetails.startTime ? new Date(sasDetails.startTime) : null;
      const expiryDate = sasDetails.expiryTime ? new Date(sasDetails.expiryTime) : null;
      const now = new Date();

      // Check validity
      const isValid = startDate && expiryDate && now >= startDate && now <= expiryDate;
      const timeUntilExpiry = expiryDate ? Math.max(0, expiryDate.getTime() - now.getTime()) : 0;
      const daysUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60 * 60 * 24));
      const hoursUntilExpiry = Math.floor((timeUntilExpiry % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      return {
        ...sasDetails,
        startDate,
        expiryDate,
        isValid,
        timeUntilExpiry,
        daysUntilExpiry,
        hoursUntilExpiry,
        formattedPermissions: {
          r: sasDetails.permissions?.includes('r') ? 'Read' : null,
          a: sasDetails.permissions?.includes('a') ? 'Add' : null,
          c: sasDetails.permissions?.includes('c') ? 'Create' : null,
          w: sasDetails.permissions?.includes('w') ? 'Write' : null,
          d: sasDetails.permissions?.includes('d') ? 'Delete' : null,
          l: sasDetails.permissions?.includes('l') ? 'List' : null,
          i: sasDetails.permissions?.includes('i') ? 'Immutable' : null
        }
      };
    } catch (error) {
      console.error('Error parsing SAS token:', error);
      return null;
    }
  };

  // Get SAS token details
  const sasTokenDetails = parseSASToken();

  // Log SAS token details on component mount
  React.useEffect(() => {
    if (sasTokenDetails) {
      console.log('=== SAS TOKEN DETAILS ===');
      console.log('Storage Account:', 'xpertstgacc.blob.core.windows.net');
      console.log('Container:', 'xps');
      console.log('Permissions:', Object.values(sasTokenDetails.formattedPermissions).filter(Boolean).join(', '));
      console.log('Start Time:', sasTokenDetails.startDate?.toLocaleString());
      console.log('Expiry Time:', sasTokenDetails.expiryDate?.toLocaleString());
      console.log('Valid:', sasTokenDetails.isValid ? 'YES' : 'NO');
      console.log('Time Until Expiry:', `${sasTokenDetails.daysUntilExpiry} days, ${sasTokenDetails.hoursUntilExpiry} hours`);
      console.log('Protocol:', sasTokenDetails.protocol);
      console.log('API Version:', sasTokenDetails.version);
      console.log('Resource Type:', sasTokenDetails.resource === 'c' ? 'Container' : sasTokenDetails.resource);
      console.log('========================');
      
      // Show warning if token expires soon
      if (sasTokenDetails.isValid && sasTokenDetails.daysUntilExpiry < 7) {
        console.warn(`⚠️ SAS Token expires in ${sasTokenDetails.daysUntilExpiry} days!`);
      }
      
      if (!sasTokenDetails.isValid) {
        console.error('❌ SAS Token is EXPIRED or INVALID!');
      }
    }
  }, [sasTokenDetails]);

  const uploadToAzure = async (file) => {
    try {
      // Check SAS token validity first
      if (!sasTokenDetails?.isValid) {
        throw new Error('SAS Token is expired or invalid. Please contact administrator to renew the token.');
      }

      // Create a sanitized folder name from applicant name
      const folderName = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      if (!folderName) {
        throw new Error('Please fill in your name first');
      }

      console.log('Uploading resume:', file.name);
      console.log('Folder name:', folderName);
      console.log('SAS Token valid until:', sasTokenDetails.expiryDate?.toLocaleString());

      // Parse the container SAS URL
      const containerUrl = AZURE_STORAGE_URL;
      
      // Create a unique blob name with applicant folder
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const blobName = `workforce-applications/${folderName}/resume_${timestamp}.${fileExtension}`;

      console.log('Blob name:', blobName);

      // Construct the full blob URL with SAS token
      const blobUrl = `${containerUrl.split('?')[0]}/${blobName}?${containerUrl.split('?')[1]}`;
      
      console.log('Blob URL (without token):', blobUrl.split('?')[0]);

      // Create BlockBlobClient directly with the blob URL
      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(blobUrl);

      console.log('Starting upload...');

      // Upload file using uploadData
      const uploadResponse = await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type
        }
      });

      console.log('Upload successful!', uploadResponse);

      return {
        url: blobUrl.split('?')[0],
        name: file.name,
        blobName: blobName
      };
    } catch (error) {
      console.error('Error uploading to Azure:', error);
      
      // Provide more specific error messages
      if (error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Azure Storage needs to allow requests from localhost. Please configure CORS settings in Azure Portal.');
      } else if (error.statusCode === 403) {
        throw new Error('Permission denied: Check SAS token permissions and expiry');
      } else if (error.statusCode === 404) {
        throw new Error('Container not found: Verify container name');
      }
      
      throw error;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Invalid File Type', {
        description: 'Please upload a PDF, DOC, or DOCX file',
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', {
        description: 'Please upload a file smaller than 5MB',
      });
      return;
    }

    // Check if name is filled before upload
    if (!formData.name.trim()) {
      toast.error('Name Required', {
        description: 'Please fill in your name before uploading resume',
      });
      return;
    }

    setUploading(true);
    
    try {
      toast.info('Uploading Resume', {
        description: 'Uploading your resume to secure storage...',
      });

      const result = await uploadToAzure(file);
      
      setResumeFile({
        file: file,
        azureUrl: result.url,
        blobName: result.blobName
      });

      toast.success('Resume Uploaded', {
        description: 'Your resume has been successfully uploaded',
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload Failed', {
        description: error.message || 'Failed to upload resume. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    // Validate form
    if (!formData.name.trim()) {
      toast.error('Name Required', {
        description: 'Please enter your full name',
      });
      return;
    }

    if (!formData.experience) {
      toast.error('Experience Required', {
        description: 'Please select your years of experience',
      });
      return;
    }

    if (!formData.location.trim()) {
      toast.error('Location Required', {
        description: 'Please enter your location',
      });
      return;
    }

    if (!resumeFile) {
      toast.error('Resume Required', {
        description: 'Please upload your resume',
      });
      return;
    }

    // Log application data
    console.log('Workforce Application Submitted:', {
      ...formData,
      resume: resumeFile
    });

    setSubmitted(true);
    
    toast.success('Application Submitted', {
      description: 'Thank you! We will review your application and get back to you soon.',
    });

    // Reset form after 2 seconds
    setTimeout(() => {
      setFormData({ name: '', experience: '', location: '' });
      setResumeFile(null);
      setSubmitted(false);
      onOpenChange(false);
    }, 2000);
  };

  const resetForm = () => {
    setFormData({ name: '', experience: '', location: '' });
    setResumeFile(null);
    setSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">Workforce Application</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-brand-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Submitted!</h3>
            <p className="text-gray-600">We'll review your application and contact you soon.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full"
              />
            </div>

            {/* Years of Experience */}
            <div className="space-y-2">
              <Label htmlFor="experience">Years of Experience *</Label>
              <Select value={formData.experience} onValueChange={(value) => handleInputChange('experience', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your experience level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-1">0-1 years (Fresher)</SelectItem>
                  <SelectItem value="1-3">1-3 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="5-8">5-8 years</SelectItem>
                  <SelectItem value="8-12">8-12 years</SelectItem>
                  <SelectItem value="12+">12+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Field */}
            <div className="space-y-2">
              <Label htmlFor="location">Current Location *</Label>
              <Input
                id="location"
                type="text"
                placeholder="City, State/Country"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full"
              />
            </div>

            {/* Resume Upload */}
            <div className="space-y-2">
              <Label htmlFor="resume">Upload Resume *</Label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                {resumeFile ? (
                  <div className="flex items-center justify-center space-x-2 text-brand-600">
                    <FileText className="w-5 h-5" />
                    <span className="text-sm font-medium">{resumeFile.file.name}</span>
                    <CheckCircle className="w-4 h-4" />
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX (Max 5MB)
                    </p>
                  </div>
                )}
                
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={uploading}
                />
                
                {uploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-20">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-3">
              <Button
                onClick={resetForm}
                variant="outline"
                className="flex-1"
              >
                Reset
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-navy-900 hover:bg-navy-800"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkforceApplicationForm;